import {
  Component,
  ElementRef,
  inject,
  model,
  OnInit,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  BoelsPointOfServiceStock,
  Cart,
  CartModification,
  CheckoutOptionEnum,
  GlobalMessageType,
  GroupOrderModification,
  SearchConfig,
  StockLevelStatusEnum,
} from '@boels/models';
import {
  CartFacade,
  CultureFacade,
  GlobalMessageFacade,
  ProductStockFacade,
} from '@boels/shared-state';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faChevronLeft,
  faLocationDot,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  map,
  Observable,
  pipe,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { SpinnerComponent } from '../../spinner/spinner.component';
import { EcommerceTrackingService, WindowRef } from '@boels/helpers';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { PointOfServiceCardComponent } from './point-of-service-card/point-of-service-card.component';
import { ButtonComponent } from '../../button/button.component';

const countStoresToShow = 5;
const searchConfig: SearchConfig = {
  pageSize: 10,
  currentPage: 0,
};
@Component({
  selector: 'boels-checkout-pick-up',
  templateUrl: './checkout-pick-up.component.html',
  styleUrls: ['./checkout-pick-up.component.scss'],
  imports: [
    FontAwesomeModule,
    FormsModule,
    TranslocoModule,
    SpinnerComponent,
    PointOfServiceCardComponent,
    ButtonComponent,
  ],
  providers: [
    ProductStockFacade,
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['checkout', 'user'],
    },
  ],
})
export class CheckoutPickUpComponent implements OnInit {
  #cartFacade = inject(CartFacade);
  #productStockFacade = inject(ProductStockFacade);
  #router = inject(Router);
  #ecommerceTrackingService = inject(EcommerceTrackingService);
  #winRef = inject(WindowRef);
  #globalMessageService = inject(GlobalMessageFacade);
  #cultureFacade = inject(CultureFacade);

  #cart$ = this.#cartFacade
    .getActiveCart()
    .pipe(filter(Boolean), takeUntilDestroyed());

  $stores: Signal<BoelsPointOfServiceStock[] | undefined> = toSignal(
    this.#getPointOfServiceStores().pipe(takeUntilDestroyed()),
  );
  $selectedStore: WritableSignal<BoelsPointOfServiceStock | undefined> =
    signal(undefined);
  $queryModel = model('');
  $dayOfWeek = signal('');

  protected readonly faLocation = faLocationDot;
  protected readonly faSearch = faSearch;
  protected readonly faChevronLeft = faChevronLeft;

  searchStoresInput =
    viewChild<ElementRef<HTMLInputElement>>('searchStoresInput');

  scrollToElement = viewChild<ElementRef<HTMLElement>>('scrollToElement');

  ngOnInit(): void {
    this.#setPointOfServiceState();
    this.#setWeekdayState();
    this.#getStoresIfNotPrefetched();
  }

  goNext(): void {
    this.#winRef.nativeWindow?.scrollTo({ top: 0, behavior: 'smooth' });
    this.#updateCart()
      .pipe(
        tap((cartMod: GroupOrderModification) => {
          if (
            cartMod.cartModifications?.some((c) => c.statusCode !== 'success')
          )
            throw 'Could not update cart mode with pickup branch';
          const option = this.#getCheckoutOption();
          this.#ecommerceTrackingService.setCheckoutOptionEvent(option);
          this.#productStockFacade.setSelectedProductStock({
            query: this.$queryModel(),
            selectedProductStock:
              this.$selectedStore() as BoelsPointOfServiceStock,
          });
          this.#router.navigate(['/checkout/review-order']);
        }),
        take(1),
        catchError((error) => {
          this.#handleError(error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  goPrevious(): void {
    this.#router.navigate(['/checkout/delivery-mode']);
  }

  searchStores(useMyLocation: boolean, $event?: KeyboardEvent): void {
    if ($event && $event.key !== 'Enter') return;

    if (useMyLocation) this.$queryModel.set('');

    // Hide the mobile keyboard on enter
    this.searchStoresInput()?.nativeElement.blur();

    // Scroll to element to make sure search results are visible
    const element = this.scrollToElement()?.nativeElement;
    if (element) {
      // Time it needed to prevent timing issues on mobile devices
      setTimeout(() => {
        // Unfocus any element
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement) {
          activeElement.blur();
        }
        this.scrollToElementWithOffset(element);
      }, 100);
    }

    this.#cart$
      .pipe(
        this.#productCodesPipe(),
        map((productCodes) =>
          this.#productStockFacade.loadClosestStores(
            productCodes,
            this.$queryModel(),
            searchConfig,
            useMyLocation,
          ),
        ),
        take(1),
      )
      .subscribe();
  }

  #updateCart(): Observable<CartModification> {
    const selectedStore = this.$selectedStore() as BoelsPointOfServiceStock;
    const pointOfServiceName = selectedStore.name;
    return selectedStore.hasRentalWall
      ? this.#cartFacade.updateCartMode(
          pointOfServiceName,
          selectedStore.pickupFromRentalWall,
        )
      : this.#cartFacade.updateCartMode(pointOfServiceName);
  }

  #getPointOfServiceStores(): Observable<BoelsPointOfServiceStock[]> {
    return this.#productStockFacade.getProductStockStores().pipe(
      map((stores: BoelsPointOfServiceStock[]) => {
        return stores
          .filter(
            (store) =>
              store.stockInfo?.stockLevelStatus ===
              StockLevelStatusEnum.IN_STOCK,
          )
          .slice(0, countStoresToShow)
          .map((store) => ({
            ...store,
            pickupFromRentalWall: false,
          }));
      }),
    );
  }

  #getCheckoutOption(): string {
    return this.$selectedStore()?.hasRentalWall
      ? this.$selectedStore()?.pickupFromRentalWall
        ? CheckoutOptionEnum.RENTAL_WALL_PICKUP
        : CheckoutOptionEnum.BRANCH_PICKUP
      : CheckoutOptionEnum.BRANCH_PICKUP;
  }

  #handleError(error?: Error): void {
    if (error) {
      this.#globalMessageService.add(
        { key: 'confirmation.error.text', scope: 'checkout' },
        GlobalMessageType.MSG_TYPE_ERROR,
      );
      const errorMessage =
        (error?.message || error) ?? 'An unknown error occurred.';
      throwError(() => errorMessage);
    }
  }

  #setPointOfServiceState(): void {
    combineLatest([
      this.#cart$.pipe(this.#queryTextPipe()),
      this.#productStockFacade.getSelectedProductStockStore(),
    ])
      .pipe(
        tap(([query, state]) => {
          if (state) {
            this.$selectedStore.set(state.store);
            this.$queryModel.set(state.query);
            const storeInList = this.$stores()?.find(
              (s) => s.name === state.store.name,
            );
            if (storeInList)
              storeInList.pickupFromRentalWall =
                state.store.pickupFromRentalWall;
            return;
          }
          this.$queryModel.set(query);
        }),
        take(1),
      )
      .subscribe();
  }

  #setWeekdayState(): void {
    this.#cart$
      .pipe(this.#startDatePipe(), this.#weekdayPipe(), take(1))
      .subscribe();
  }

  #getStoresIfNotPrefetched(): void {
    this.#productStockFacade
      .getProductStockState()
      .pipe(
        tap((state) => {
          if (
            !state.loading &&
            !state.success &&
            !state.error &&
            state.stores.length === 0
          )
            this.searchStores(false);
        }),
        take(1),
      )
      .subscribe();
  }

  //#region observable utils
  #productCodesPipe() {
    return pipe(
      map((cart: Cart) =>
        cart.entries
          ? cart.entries
              .flatMap((entry) => entry.product?.code)
              .filter((code): code is string => code !== undefined)
          : [],
      ),
    );
  }

  #queryTextPipe() {
    return pipe(
      map((cart: Cart) => cart.paymentInfo?.billingAddress?.formattedAddress),
      filter(Boolean),
      tap((query: string) => this.$queryModel.set(query)),
    );
  }

  #startDatePipe() {
    return pipe(
      map((cart: Cart) => cart.startDate),
      filter(Boolean),
    );
  }

  #weekdayPipe() {
    return pipe(
      switchMap((startDate: string) =>
        this.#cultureFacade.getActiveLanguage().pipe(
          filter(Boolean),
          tap((language: string) => {
            const lang = language.substring(0, 2);
            const date = new Date(startDate);
            const dayOfTheWeek = date
              .toLocaleString(lang, {
                timeZone: 'UTC',
                weekday: 'short',
              })
              .substring(0, 2);
            this.$dayOfWeek.set(dayOfTheWeek);
          }),
          take(1),
        ),
      ),
    );
  }

  scrollToElementWithOffset(element: HTMLElement, offset = 16): void {
    const top = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}
